import React, { useState, useRef } from 'react'
import { JOURS, semaineCourante } from '../lib/helpers'

export default function ImportPhoto({ defis, onImport }) {
  const [loading, setLoading] = useState(false)
  const [resultat, setResultat] = useState(null)
  const [erreur, setErreur] = useState('')
  const fileRef = useRef(null)

  const analyser = async (file) => {
    setLoading(true)
    setErreur('')
    setResultat(null)

    try {
      // Convertir en base64 — si HEIC, on passe par canvas pour convertir en JPEG
      let base64, mediaTypeForced
      const supportedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (supportedTypes.includes(file.type)) {
        base64 = await new Promise((res, rej) => {
          const r = new FileReader()
          r.onload = () => res(r.result.split(',')[1])
          r.onerror = rej
          r.readAsDataURL(file)
        })
        mediaTypeForced = file.type
      } else {
        // HEIC ou autre format non supporté : conversion via canvas
        const bitmap = await createImageBitmap(file)
        const canvas = document.createElement('canvas')
        canvas.width = bitmap.width
        canvas.height = bitmap.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(bitmap, 0, 0)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
        base64 = dataUrl.split(',')[1]
        mediaTypeForced = 'image/jpeg'
        bitmap.close()
      }

      // Préparer la liste des défis pour Claude
      const listeDefis = defis.filter(d => d.actif).map(d =>
        `- "${d.label}" (id: ${d.id}, ${d.points} pts/jour)`
      ).join('\n')

      const prompt = `Tu es un assistant qui analyse des photos de tableaux de valorisation pour enfants.

Voici les défis présents dans ce tableau :
${listeDefis}
- "bonus" (points bonus)

Les jours de la semaine sont : Lun, Mar, Mer, Jeu, Ven, Sam, Dim

Analyse cette photo du tableau et identifie pour chaque combinaison (jour, défi) si la case est cochée ou non.

Réponds UNIQUEMENT avec un objet JSON valide, sans aucun texte avant ou après, de ce format exact :
{
  "saisies": [
    {"jour": "Lun", "defiId": "id_du_defi", "valeur": 2},
    {"jour": "Mar", "defiId": "id_du_defi", "valeur": 0}
  ],
  "semaine": "${semaineCourante()}"
}

N'inclus que les cases qui sont clairement cochées (valeur > 0). Si une case est vide ou illisible, ne l'inclus pas.`

      const response = await fetch('/api/analyze-photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64,
          mediaType: mediaTypeForced,
          prompt
        })
      })

      const data = await response.json()
      if (data.error) throw new Error(data.error)
      const text = data.content?.find(c => c.type === 'text')?.text || ''
      const clean = text.replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(clean)

      setResultat(parsed)
      setLoading(false)
    } catch (e) {
      setErreur('Impossible de lire le tableau. Essaie avec une photo plus nette.')
      setLoading(false)
    }
  }

  const confirmer = () => {
    if (resultat) {
      onImport(resultat)
      setResultat(null)
    }
  }

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && analyser(e.target.files[0])} />

      <button onClick={() => fileRef.current.click()} disabled={loading} style={{
        padding: '8px 16px', borderRadius: 8, border: '1.5px solid #7c3aed',
        background: '#fff', color: '#7c3aed', fontSize: 12,
        fontWeight: 600, cursor: 'pointer', display: 'flex',
        alignItems: 'center', gap: 6, fontFamily: "'Inter', sans-serif"
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
        {loading ? 'Analyse...' : 'Saisie auto par photo du tableau imprimé'}
      </button>

      {loading && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#7c3aed', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
          Analyse automatique du tableau en cours...
        </div>
      )}

      {erreur && (
        <div style={{ marginTop: 10, background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600 }}>
          {erreur}
        </div>
      )}

      {resultat && (
        <div style={{ marginTop: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14 }}>
          <div style={{ fontWeight: 700, color: '#166534', marginBottom: 6, fontSize: 13 }}>
            {resultat.saisies.length} cases détectées automatiquement
          </div>
          <div style={{ fontSize: 12, color: '#166534', marginBottom: 10 }}>
            Vérifiez que les données correspondent avant de valider.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={confirmer} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
              Valider l'import
            </button>
            <button onClick={() => setResultat(null)} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
